import api from "app/lib/api";
import {
  BASE_URL,
  DEFAULT_NEXT_TRANSMISSION_CYCLE_DAYS,
} from "static/constants";
import endpoints from "static/endpoints";
import formatDate from "utils/formatDate";
import { interpolate } from "utils/string";

export const patientBySerialNumber = async (serialNumber, isPatientModal) => {
  if (serialNumber && isPatientModal) {
    const { data } = await api.get(
      `${endpoints.patient.profile}/${serialNumber}`
    );

    return data;
  }
};

export const patientByPatientId = async (patientId) => {
  let url = interpolate(endpoints.patient.fetchByPatientId, { patientId });
  const response = await api.get(url);
  return response;
};

export const patientReports = async (
  serialNumber,
  page,
  perPage,
  search,
  account
) => {
  let url = `${BASE_URL}/v2/patient/${serialNumber}/reports?`;

  if (account) url += `account=${account}&`;

  if (search) url += `search=${search}&`;

  url += `page=${page}&perPage=${perPage}`;

  if (serialNumber) {
    const { data } = await api.get(url);

    return data;
  }
};

export const getPatientTasks = async ({
  patientId,
  page,
  pageSize,
  search,
}) => {
  let url = `${BASE_URL}/v2/patient/${patientId}/tasks?`;

  if (search) url += `search=${search}&`;

  url += `page=${page}&perPage=${pageSize}`;

  if (patientId) {
    const { data } = await api.get(url);

    return data;
  }
};

export const getPatientDocuments = async (
  serialNumber,
  page,
  perPage,
  search
) => {
  let url = interpolate(endpoints.patient.fetchDocuments, { serialNumber });

  //TODO: implement server side pagination
  // if (search) url += `search=${search}&`;

  // url += `?page=${page}&perPage=${perPage}`;

  const response = await api.get(url);

  return response;
};

export const uploadPatientDocument = async (payload) => {
  const url = endpoints.patient.uploadDocument;

  const formData = new FormData();
  Object.keys(payload).forEach((item) => {
    formData.append(item, payload[item]);
  });

  const response = await api.post(url, formData);
  return response;
};

export const fetchPatientDocument = async (filePath) => {
  const url = `${endpoints.patient.fetchDocumentSignedURL}?filePath=${filePath}`;
  const response = await api.get(url, { responseType: "blob" });
  return response;
};

export const updateBasicInformation = async (
  serialNumber,
  profile,
  patientAlertInformation
) => {
  let payload = {
    account: profile?.account,
    dob: profile?.dob ? formatDate(profile?.dob, "MM/DD/YYYY") : "",
    doi: profile?.doi ? formatDate(profile?.doi, "MM/DD/YYYY") : "",
    firstName: profile?.firstName,
    gender: profile?.gender,
    lastName: profile?.lastName,
    middleInitial: profile?.middleInitial,
    mrn: profile?.mrn,
    genderIdentity: profile?.genderIdentity,
    marital_status: profile?.marital_status || "",
    ssn: profile?.ssn || "",
    sexAssignedAtBirth: profile?.sexAssignedAtBirth,
    physicians: profile?.physicians || [],
    active: profile?.active,
    patientAlertInformation,
    hospital: profile?.hospital,
    deactivate_reason: profile?.deactivate_reason,
    referringProviderId: profile?.referringProviderId,
    preferredLanguage: profile?.preferredLanguage,
    alertWhenDisconnected: profile?.alertWhenDisconnected,
    monthlyBatteryCheck: profile?.monthlyBatteryCheck,
    inOfficeOnly: profile?.inOfficeOnly,
    statusLetter: profile?.statusLetter,
  };

  const keysToCopy = ["need_magnet_during_procedures", "laao_device", "idtf", "alertsOnly"];

  for (const key of keysToCopy) {
    if (Object.hasOwn(profile, key) && profile[key] !== null) {
      payload[key] = profile[key];
    }
  }

  if (
    "hfToClinic" in profile
    // || "regularToClinic" in profile
  ) {
    payload.initialReportTab = {
      hf: profile?.hfToClinic,
      // regular: profile?.regularToClinic,
    };
  }

  if (serialNumber) {
    const response = await api.put(
      `${BASE_URL}/v2/patient/profile/${serialNumber}`,
      payload
    );

    return response;
  }
};

export const updatePatientBasicInformation = async (serialNumber, payload) => {
  const url = interpolate(endpoints.patient.patientInformation, {
    serialNumber,
  });

  const response = await api.patch(url, payload);
  return response;
};

export const updatePatientTransmission = async (serialNumber, profile) => {
  const payload = {
    last_send: profile?.last_send || "",
    next_send: profile?.next_send || "",
    nextSendDays:
      Number(profile?.nextSendDays) || DEFAULT_NEXT_TRANSMISSION_CYCLE_DAYS,
    nextBillingDateChanged: profile?.nextBillingDateChanged,
    last_bill_date: profile?.last_bill_date
      ? formatDate(profile?.last_bill_date, "MM/DD/YYYY")
      : "",
    next_bill_date: profile?.next_bill_date
      ? formatDate(profile?.next_bill_date, "MM/DD/YYYY")
      : "",
    total_sends: profile?.total_sends,
  };

  if (serialNumber) {
    const response = await api.put(
      `${BASE_URL}/v2/patient/transmission/${serialNumber}`,
      payload
    );

    return response;
  }
};

export const updateBillingSchedule = async (
  patientId,
  days,
  active,
  billingCode
) => {
  if (patientId) {
    const response = await api.put(`${BASE_URL}/patient/updateschedule`, {
      patientId,
      days,
      active,
      billingCode,
    });

    return response;
  }
};

export const updateMultipleBillingSchedule = async (payload) => {
  if (payload && payload.billingSchedule.length > 0) {
    const response = await api.put(
      endpoints.patient.updateBillingSchedule,
      payload
    );

    return response;
  }
};

export const updatePatientInformation = async (serialNumber, information) => {
  if (serialNumber) {
    const response = await api.put(`${BASE_URL}/patient/information`, {
      patient: serialNumber,
      information,
    });

    return response;
  }
};

export const updatePatientDeviceInformation = async (payload) => {
  if (!payload?.patient) {
    return;
  }
  const response = await api.put(
    `${BASE_URL}/patient/device-information`,
    payload
  );
  return response;
};

export const updatePatientInsurance = async (serialNumber, insurance, type) => {
  if (serialNumber) {
    const payload = {
      name: insurance.name,
      patientId: insurance.patientId,
      patientGroup: insurance.patientGroup,
      plan: insurance.plan,
      patientRelation: insurance.patientRelation,
      effectiveDate: insurance.effectiveDate ?? "",
      authorization_code: insurance.authorization_code ?? "",
      treatment_start: insurance.treatment_start ?? "",
      treatment_end: insurance.treatment_end ?? "",
      payor_id: insurance.payor_id,
      active: insurance.active,
      insured: {
        first_name: insurance?.insured?.first_name || "",
        last_name: insurance?.insured?.last_name || "",
        dob: insurance.insured.dob ?? "",
        gender: insurance.insured.gender,
        street: insurance.insured.street,
        city: insurance.insured.city,
        state: insurance.insured.state,
        zip: insurance.insured.zip,
      },
    };

    const response = await api.put(`${BASE_URL}/v2/patient/insurance`, {
      serialNumber: serialNumber,
      insuranceType: type,
      insurance: payload,
    });

    return response;
  }
};

export const updatePatientTask = async (patientId, task, isEditTask) => {
  if (patientId) {
    let response;

    if (isEditTask) {
      const { _id, ...rest } = task;
      response = await api.put(`${BASE_URL}/task/${_id}`, {
        patientId,
        ...rest,
      });
    } else {
      response = await api.post(`${BASE_URL}/task`, {
        patientId,
        ...task,
      });
    }

    return response;
  }
};

export const updatePatientAlertBanner = async (serialNumber, bannerText) => {
  const url = interpolate(endpoints.reportBanners, { serialNumber });

  if (serialNumber) {
    const response = await api.put(url, {
      patientSerialNumber: serialNumber,
      bannerText,
    });

    return response;
  }
};

export const updateHeartFailure = async (serialNumber) => {
  if (serialNumber) {
    const response = await api.put(
      `${BASE_URL}/patient/centra-heart-failure/${serialNumber}`
    );

    return response;
  }
};

export const fetchPatientAnalytics = async (patientId) => {
  let url = `${BASE_URL}/v2/patient/analytics/${patientId}`;

  if (patientId) {
    const { data } = await api.get(url);

    return data;
  }
};

export const fetchPatientBpReadings = async (patientId, filter) => {
  let url = `${BASE_URL}/v2/rpm/bp-reading/${patientId}`;

  if (filter) url += `?filter=${filter}`;

  if (patientId) {
    const { data } = await api.get(url);

    return data;
  }
};

export const updatePatientHistoryInformation = async (patientId, payload) => {
  let url = interpolate(endpoints.patient.updatePatientHistory, { patientId });

  const response = await api.put(url, payload);
  return response;
};

export const sendLetter = async (serialNumber, filePath, documentId) => {
  let url = interpolate(endpoints.patient.sendLetter, { serialNumber });

  const response = await api.post(url, { filePath, documentId });
  return response;
};

export const fetchPatientLeadInformation = async (patientId) => {
  let url = interpolate(endpoints.patient.leadInformation, { patientId });

  if (patientId) {
    const { data } = await api.get(url);

    return data;
  }
};

export const updatePatientLeadInformation = async (patientId, leads) => {
  let url = interpolate(endpoints.patient.leadInformation, { patientId });

  if (patientId) {
    return await api.put(url, { leads });
  }
};

export const addNewPatientDevice = async (patientId, payload) => {
  let url = interpolate(endpoints.patient.addNewDevice, { id: patientId });
  return await api.post(url, payload);
};

export const createPatient = async ({
  serialNumber,
  modelName,
  modelNumber,
  deviceType,
}) => {
  let url = interpolate(endpoints.patient.createPatient, { serialNumber });

  return await api.post(url, {
    modelName,
    modelNumber,
    deviceType,
  });
};

export const getReminderLogs = async ({
  patientId,
  type,
  page,
  pageSize,
  search,
}) => {
  let url = `${BASE_URL}/v2/patient/reminder-logs/${patientId}?`;

  if (search) url += `search=${search}&`;
  if (type) url += `logType=${type}&`;

  url += `page=${page}&pageSize=${pageSize}`;

  if (patientId) {
    const { data, meta } = await api.get(url);

    return { data, meta };
  }
};

export const updateSubscribeSetting = async (patientId, subscribeSetting) => {
  let url = interpolate(endpoints.patient.subscribeSetting, { patientId });
  const { data, message } = await api.put(url, subscribeSetting);

  return { data, message };
};

export const fetchBenchmarkPatientInsurance = async (benchmarkId, evokeId) => {
  let url = `${endpoints.insurances.benchmarkInsurances}?evokeId=${evokeId}&benchmarkId=${benchmarkId}`;
  const { data } = await api.get(url);
  return data;
};

export const fetchPatientById = async (patientId, isPatientModal) => {
  if (patientId && isPatientModal) {
    const { data } = await api.get(`${endpoints.patient.profile}/${patientId}`);
    return data;
  }
};
